// IND-023: «Diferencia de inventario» en Datos de planta: por material, lo
// que PROA calculaba en la fecha del recuento frente a lo contado ese día.
// Lo calculado es una foto guardada al cargar el recuento; lo contado se lee
// en vivo. La diferencia es merma real o escandallo mal ajustado.
import { Fragment, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  computeInventoryDifference,
  getInventoryDifference,
  listInventoryDifferenceDates,
} from '../services/industrialCostService';
import type { InventoryDifferenceRow } from '../services/industrialCostService';
import { formatDataDay } from '../utils/dailyLoadRunFile';
import { formatEsNumber } from '../utils/numberFormat';
import { sortRows, useTableSort } from '../utils/tableSort';
import SortableHeaderCell from '../components/SortableHeaderCell';

export const INVENTORY_DIFFERENCES_QUERY_KEY = ['industrial-cost-inventory-differences'];

type SortKey = 'material' | 'calculated' | 'counted' | 'difference' | 'pct' | 'pctConsumption' | 'entries' | 'consumed';

const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: 'material', label: 'Material' },
  { key: 'calculated', label: 'Calculado' },
  { key: 'counted', label: 'Contado' },
  { key: 'difference', label: 'Diferencia' },
  { key: 'pct', label: '% s/ calculado' },
  { key: 'pctConsumption', label: '% s/ consumo' },
  { key: 'entries', label: 'Entradas' },
  { key: 'consumed', label: 'Consumo' },
];
const ROW_HEIGHT = 40;

function qty(value: number | null | undefined): string {
  return value == null ? '—' : formatEsNumber(value, { maximumFractionDigits: 1 });
}

function pct(value: number | null | undefined): string {
  if (value == null) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatEsNumber(value, { maximumFractionDigits: 1 })} %`;
}

function sortValue(row: InventoryDifferenceRow, key: SortKey): number | string {
  switch (key) {
    case 'material':
      return `${row.material_code ?? ''} ${row.material_name ?? ''}`;
    case 'calculated':
      return row.calculated_qty ?? 0;
    case 'counted':
      return row.counted_qty ?? 0;
    case 'difference':
      return Math.abs(row.difference_qty ?? 0);
    case 'pct':
      return Math.abs(row.difference_pct ?? 0);
    case 'pctConsumption':
      return Math.abs(row.difference_consumption_pct ?? 0);
    case 'entries':
      return row.entries_qty ?? 0;
    default:
      return row.consumed_qty ?? 0;
  }
}

function RowDetail({ row }: { row: InventoryDifferenceRow }) {
  const unit = row.unit_label ?? '';
  const consumers = row.breakdown ?? [];
  return (
    <Box sx={{ px: 1.5, py: 1, bgcolor: 'rgba(15,23,42,0.6)' }}>
      <Typography sx={{ color: '#cbd5e1', fontSize: 12 }}>
        {`Recuento ${qty(row.baseline_qty)} del ${formatDataDay(row.baseline_date) ?? '—'}`}
        {row.baseline_assumed_zero ? ' (no estaba en ese recuento: se tomó 0)' : ''}
        {` + entradas ${qty(row.entries_qty)}`}
        {(row.returns_qty ?? 0) < 0 ? ` (devoluciones ${qty(row.returns_qty)})` : ''}
        {` − consumo ${qty(row.consumed_qty)} = calculado ${qty(row.calculated_qty)} ${unit}`}
        {row.partial_consumption ? ' · hay producción sin fecha exacta' : ''}
      </Typography>
      {(row.evolution ?? []).length > 0 ? (
        <Typography sx={{ color: '#cbd5e1', fontSize: 12, mt: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 800 }}>Evolución: </Box>
          {(row.evolution ?? [])
            .map((item) => `${formatDataDay(item.count_date)?.slice(0, 5)} ${pct(item.difference_pct)}`)
            .join(' · ')}
        </Typography>
      ) : null}
      <Typography sx={{ color: '#94a3b8', fontSize: 11.5, fontWeight: 800, mt: 0.75 }}>
        Productos que lo consumieron en el periodo:
      </Typography>
      {consumers.length === 0 ? (
        <Typography sx={{ color: '#64748b', fontSize: 12 }}>Ninguno.</Typography>
      ) : (
        <Stack spacing={0.25} sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {consumers.map((consumer) => (
            <Typography key={`${consumer.product_id}-${consumer.customer_number ?? ''}-${consumer.basis_unit ?? ''}`} sx={{ color: '#94a3b8', fontSize: 12 }}>
              {[
                consumer.customer_number
                  ? `${consumer.product_code ?? consumer.product_name ?? '—'} · cliente ${consumer.customer_number}`
                  : consumer.product_code ?? consumer.product_name ?? '—',
                `${qty(consumer.produced_qty)} ${consumer.basis_unit ?? 'm'} fabricados`,
                consumer.consumption_per_unit != null
                  ? `${formatEsNumber(consumer.consumption_per_unit, { maximumFractionDigits: 4 })} ${unit}/${consumer.basis_unit ?? 'm'}`
                  : null,
                consumer.multiple_rules ? 'varias reglas' : null,
                `total ${qty(consumer.consumed_qty)} ${unit}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function SideList({ title, rows, counted }: { title: string; rows: InventoryDifferenceRow[]; counted: boolean }) {
  if (rows.length === 0) return null;
  return (
    <Box>
      <Typography sx={{ color: '#f8fafc', fontSize: 13, fontWeight: 800 }}>{`${title} · ${rows.length}`}</Typography>
      <Box sx={{ maxHeight: 160, overflowY: 'auto' }}>
        {rows.map((row) => (
          <Typography key={row.material_id} sx={{ color: '#94a3b8', fontSize: 12 }}>
            {[
              row.material_code,
              row.material_name,
              counted ? `contado ${qty(row.counted_qty)} ${row.unit_label ?? ''}` : `calculado ${qty(row.calculated_qty)} ${row.unit_label ?? ''}`,
              row.reason === 'nuevo'
                ? 'nuevo en el periodo'
                : row.reason === 'parcial'
                  ? 'hay producción sin fecha exacta'
                  : row.reason === 'sin_base'
                    ? 'sin recuento anterior'
                    : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

export default function IndustrialCostInventoryDifferenceBlock() {
  const queryClient = useQueryClient();
  const datesQuery = useQuery({
    queryKey: [...INVENTORY_DIFFERENCES_QUERY_KEY, 'dates'],
    queryFn: listInventoryDifferenceDates,
    staleTime: 60_000,
    retry: false,
  });
  const dates = datesQuery.data ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  // Por defecto, el último recuento.
  const countDate = selected ?? dates[0]?.count_date ?? null;
  const current = dates.find((item) => item.count_date === countDate);

  const differenceQuery = useQuery({
    queryKey: [...INVENTORY_DIFFERENCES_QUERY_KEY, 'detail', countDate],
    queryFn: () => getInventoryDifference(countDate!),
    enabled: Boolean(countDate && current?.has_snapshot),
    staleTime: 60_000,
    retry: false,
  });
  const compute = useMutation({
    mutationFn: (redo: boolean) => computeInventoryDifference(countDate!, redo),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: INVENTORY_DIFFERENCES_QUERY_KEY }),
  });

  const [search, setSearch] = useState('');
  const sort = useTableSort<SortKey>(null, 'desc');
  const [openId, setOpenId] = useState<string | null>(null);
  const data = differenceQuery.data;

  const rows = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('es');
    const filtered = (data?.rows ?? []).filter((row) =>
      !needle || `${row.material_code ?? ''} ${row.material_name ?? ''}`.toLocaleLowerCase('es').includes(needle),
    );
    // Sin orden elegido, el del backend: anomalías y después |diferencia %|.
    const key = sort.key;
    if (!key) return filtered;
    return sortRows(filtered, (row) => sortValue(row, key), sort.direction);
  }, [data, search, sort.key, sort.direction]);

  return (
    <Paper sx={{ borderRadius: 4, bgcolor: 'rgba(15,23,42,0.72)', border: '1px solid rgba(148,163,184,0.16)' }}>
      <Stack spacing={1.25} sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
          <Box>
            <Typography sx={{ color: '#f8fafc', fontSize: 18, fontWeight: 900 }}>Diferencia de inventario</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 12 }}>
              Lo que PROA calculaba el día del recuento frente a lo contado: merma real o escandallo mal ajustado.
            </Typography>
          </Box>
          {dates.length > 0 ? (
            <TextField
              select
              size="small"
              label="Recuento"
              value={countDate ?? ''}
              onChange={(event) => {
                setSelected(event.target.value);
                setOpenId(null);
                compute.reset();
              }}
              sx={{ minWidth: 200 }}
            >
              {dates.map((item) => (
                <MenuItem key={item.count_date} value={item.count_date}>
                  {`${formatDataDay(item.count_date)}${item.has_snapshot ? '' : ' · sin comparación'}`}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Stack>

        {datesQuery.isLoading ? <CircularProgress size={20} /> : null}
        {datesQuery.isError ? (
          <Alert severity="error" variant="outlined">
            No se pudieron leer las fechas de recuento.
          </Alert>
        ) : null}
        {differenceQuery.isError ? (
          <Alert severity="error" variant="outlined">
            No se pudo cargar la comparación de este recuento.
          </Alert>
        ) : null}
        {!datesQuery.isLoading && !datesQuery.isError && dates.length === 0 ? (
          <Typography sx={{ color: '#64748b', fontSize: 12 }}>Todavía no hay recuentos cargados.</Typography>
        ) : null}

        {current && !current.has_snapshot ? (
          <Alert
            severity="info"
            variant="outlined"
            action={
              current.can_compute ? (
                <Button color="inherit" size="small" disabled={compute.isPending} onClick={() => compute.mutate(false)}>
                  {compute.isPending ? 'Calculando…' : 'Calcular comparación'}
                </Button>
              ) : undefined
            }
          >
            {current.can_compute
              ? 'Este recuento no tiene comparación guardada. Se puede calcular ahora con los escandallos y tablas de hoy.'
              : 'Es el primer recuento: no hay uno anterior con el que comparar.'}
          </Alert>
        ) : null}
        {compute.isError ? (
          <Alert severity="error" variant="outlined">
            {String((compute.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'No se pudo calcular la comparación.')}
          </Alert>
        ) : null}

        {differenceQuery.isLoading ? <CircularProgress size={20} /> : null}
        {data ? (
          <>
            <Typography sx={{ color: '#cbd5e1', fontSize: 12.5 }}>
              {`Recuento del ${formatDataDay(data.snapshot.count_date)} frente al calculado desde el recuento del ${formatDataDay(data.snapshot.base_date)}`}
              {` (entradas y producción del ${formatDataDay(data.snapshot.period_from)} al ${formatDataDay(data.snapshot.period_to)}).`}
              {data.snapshot.kind === 'retroactive' ? ` ${data.snapshot.warnings.join(' ')}` : ''}
            </Typography>
            {data.snapshot.kind === 'retroactive' ? (
              <Box>
                <Button
                  size="small"
                  disabled={compute.isPending}
                  onClick={() => {
                    if (window.confirm('¿Rehacer la comparación con los escandallos y tablas de hoy?')) compute.mutate(true);
                  }}
                  sx={{ textTransform: 'none', px: 0 }}
                >
                  Rehacer comparación
                </Button>
              </Box>
            ) : null}
            {data.gaps.has_gaps ? (
              <Alert severity="warning" variant="outlined">
                {'Periodo con huecos: '}
                {[
                  data.gaps.workdays_without_production.length > 0
                    ? `días laborables sin producción: ${data.gaps.workdays_without_production.map((day) => formatDataDay(day)?.slice(0, 5)).join(', ')}`
                    : null,
                  data.gaps.production_until
                    ? `la producción llega hasta el ${formatDataDay(data.gaps.production_until)}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                . Lo calculado puede no estar completo.
              </Alert>
            ) : null}
            {data.snapshot.data_changed ? (
              <Alert severity="info" variant="outlined">
                Los partes o albaranes del periodo han cambiado desde que se guardó la comparación: la foto no se actualiza.
              </Alert>
            ) : null}

            <TextField
              size="small"
              placeholder="Buscar material por código o nombre"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ width: { xs: '100%', sm: 320 }, '& input': { fontSize: 13 } }}
            />
            <Box
              sx={{
                maxHeight: ROW_HEIGHT * 11,
                overflow: 'auto',
                border: '1px solid rgba(148,163,184,0.16)',
                borderRadius: 1.5,
              }}
            >
              <Table size="small" stickyHeader sx={{ '& td, & th': { borderColor: 'rgba(148,163,184,0.12)' } }}>
                <TableHead>
                  <TableRow>
                    {COLUMNS.map((column) => (
                      <SortableHeaderCell
                        key={column.key}
                        label={column.label}
                        columnKey={column.key}
                        sort={sort}
                        align={column.key === 'material' ? 'left' : 'right'}
                        firstDirection={column.key === 'material' ? 'asc' : 'desc'}
                        sx={{ bgcolor: '#0f172a', color: '#94a3b8', fontSize: 12, py: 0.75, whiteSpace: 'nowrap' }}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length} sx={{ color: '#64748b', fontSize: 12 }}>
                        Ningún material coincide.
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {rows.map((row) => {
                    const open = openId === row.material_id;
                    const cell = { color: '#e2e8f0', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
                    const diffColor = (row.difference_qty ?? 0) < 0 ? '#fca5a5' : (row.difference_qty ?? 0) > 0 ? '#86efac' : '#e2e8f0';
                    return (
                      <Fragment key={row.material_id}>
                        <TableRow
                          hover
                          selected={open}
                          onClick={() => setOpenId(open ? null : row.material_id)}
                          sx={{ cursor: 'pointer', height: ROW_HEIGHT }}
                        >
                          <TableCell sx={{ ...cell, whiteSpace: 'normal', maxWidth: 260 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#f8fafc' }} noWrap title={row.material_name ?? ''}>
                              {row.material_code ?? '—'}
                              {row.anomaly ? ' ⚠' : ''}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>
                              {[row.material_name, row.unit_label].filter(Boolean).join(' · ')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={cell} align="right">{qty(row.calculated_qty)}</TableCell>
                          <TableCell sx={cell} align="right">{qty(row.counted_qty)}</TableCell>
                          <TableCell sx={{ ...cell, color: diffColor, fontWeight: 800 }} align="right">{qty(row.difference_qty)}</TableCell>
                          <TableCell sx={{ ...cell, color: diffColor }} align="right">{pct(row.difference_pct)}</TableCell>
                          <TableCell sx={cell} align="right">{pct(row.difference_consumption_pct)}</TableCell>
                          <TableCell sx={cell} align="right">{qty(row.entries_qty)}</TableCell>
                          <TableCell sx={cell} align="right">{qty(row.consumed_qty)}</TableCell>
                        </TableRow>
                        {open ? (
                          <TableRow>
                            <TableCell colSpan={COLUMNS.length} sx={{ p: 0 }}>
                              <RowDetail row={row} />
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Typography sx={{ color: '#64748b', fontSize: 11.5 }}>
              Diferencia = contado − calculado (negativa: falta material). ⚠ = calculado 0 o negativo (sin %). Pulsa un material para ver qué productos lo consumieron.
            </Typography>
            <SideList title="Sin stock calculado (sin base o nuevos en el periodo)" rows={data.without_stock} counted />
            <SideList title={`No están en el recuento del ${formatDataDay(data.snapshot.count_date)}`} rows={data.not_counted} counted={false} />
          </>
        ) : null}
      </Stack>
    </Paper>
  );
}
